import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Filter from "leo-profanity";
import defaultDictionary from "leo-profanity/dictionary/default.json";


// Img
import defaultImg from "../../../assets/png/no-frame.png";
import previewImg from "../../../assets/landscape.jpg";

// css
import classes from "./CreateWatermark.module.css";

// Jds
import {
  Button,
  Container,
  InputDropdown,
  InputField,
  Notifications,
  Text,
} from "@jds/core";
import { RadioGroup } from "../RadioGroup";
import { Box } from "@mui/material";

// Local
import CardOverlay from "../Cards/CardOverlay";
import useApiHttp from "../../../hooks/use-http";
import useInput from "../../../hooks/use-input";
import { imgValidationCheck } from "../../../util/helper";
import { validateTextInput } from "../../../util/validation";
import PageLoader from "../Loader/PageLoader";
import { useDispatch, useSelector } from "react-redux";
import { setPremiumAlertModalClose } from "../../../store/state-slices/plans";


// --------------------
// Watermark validation helpers (FIXED)
// --------------------

Filter.clearList();

const customProfanityWords = [
  "idiot",
  "stupid",
  "dumb",
  "fool",
  "nonsense",
  "brainless",
  "careless",
  "immature",
  "jerk",
  "loser",
  "moron",
  "creep",
  "annoying",
  "useless",
  "pathetic",
  "trash",
  "garbage",
  "scumbag",
  "coward",
]

const INDIAN_PROFANITY = [
  "हिंदीगाली",
  "தமிழ்கெட்டவார்த்தை",
  "తెలుగుదూషణ",
  "ಕನ್ನಡಅಪಶಬ್ದ",
  "മലയാളംഅപശബ്ദം",
]

// Load built-in profanity words (English + common slang)
Filter.add([...defaultDictionary, ...customProfanityWords]);


const normalize = (text) =>
  text
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();

const containsIndianProfanity = (text) => {
  return INDIAN_PROFANITY.some((word) =>
    text.includes(word)
  );
};


export const isValidWatermarkText = (val) => {
  if (
    val === undefined ||
    val === null ||
    val.trim() === "" ||
    val.length < 3 ||
    val.length > 25
  ) {
    return false;
  }

  // Unicode-safe (Indian + foreign languages)
  const unicodeSafeRegex = /^[\p{L}\p{M}\p{N}\p{P}\p{Zs}]+$/u;
  if (!unicodeSafeRegex.test(val)) return false;

  const cleaned = normalize(val);

  // ✅ English / Latin profanity
  if (Filter.check(cleaned)) return false;

  // ✅ Indian language profanity (MANUAL CHECK)
  if (containsIndianProfanity(cleaned)) return false;

  return true;
};




export default function CreateWatermark(props) {
  const { id: eventId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { premiumPlanId } = useSelector((state) => state.plans);
  const [watermarkData, setWatermarkData] = useState(null);
  const [watermarkType, setWatermarkType] = useState("TEXT");
  const [watermarkPosition, setWatermarPosition] = useState("BOTTOM_RIGHT");
  const watermarkFileInputRef = useRef(null);
  const [watermarkImage, setWatermarkImage] = useState(null);
  const [watermarkImagePrev, setWatermarkImagePrev] = useState(null);
  const [watermarkFontSize, setWatermarkFontSize] = useState(14)

  const {
    sendRequest: getWateramrkDetails,
    isLoading: isWatermarkDetailsLoading,
  } = useApiHttp();

  const {
    sendRequest: createWatermarkRequest,
    isLoading: isWatermarkCreating,
  } = useApiHttp();

  const {
    sendRequest: updateWatermarkRequest,
    isLoading: isWatermarkUpdating,
  } = useApiHttp();

  const {
    sendRequest: deleteWatermarkRequest,
    isLoading: isWatermarkDeleting,
  } = useApiHttp();

  const radioGroupProps = {
    label: "Label",
    suffix: "ic_profile",
    prefix: "ic_profile",
    size: "medium",
    options: [
      { label: "Text", value: "TEXT" },
      { label: "Image", value: "IMAGE" },
    ],
  };

  const {
    value: enteredName,
    isValid: enteredNameIsValid,
    hasError: enteredNameHasError,
    valueChangeHandler: nameChangeHandler,
    inputBlurHandler: nameBlurHandler,
    reset: resetNameInput,
  } = useInput(isValidWatermarkText, {
    type: "text",
    value: watermarkData?.watermarkText || "JioPhotos",
  });


  const {
    value: selectWatermarkPosition,
    isValid: selectedWatermarkPositionIsValid,
    hasError: selectedWatermarkPositionHasError,
    valueChangeHandler: watermarkPositionChangeHandler,
    inputBlurHandler: WatermarkPositionBlurHandler,
    reset: resetWatermarkPositionInput,
  } = useInput(validateTextInput, {
    type: "text",
    value: watermarkPosition,
  });

  const handleUploadFrameClick = () => {
    watermarkFileInputRef.current.click();
  };

  const hadleFrameFileChange = (e) => {
    const selectedFile = e.target.files[0];
    console.log(selectedFile);

    // img validation
    if (!imgValidationCheck(null, selectedFile)) {
      Notifications.toast.error({
        id: "watermark-input-img",
        title: "Please upload a valid image file (JPG, JPEG and PNG).",
      });
      e.target.value = "";
      return;
    }

    if (selectedFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setWatermarkImagePrev(reader.result); // Set the preview URL
      };
      reader.readAsDataURL(selectedFile);
    }

    setWatermarkImage(selectedFile);

    // Reset input value to trigger onchange for same file
    e.target.value = "";
  };

  let isFormValid = false;

  if (watermarkType) {
    if (watermarkType === "TEXT" && enteredNameIsValid) isFormValid = true;
    if (watermarkType === "IMAGE" && watermarkImagePrev) isFormValid = true;
  }

  const getWatermarkRequestHandler = () => {
    getWateramrkDetails(
      {
        url: `watermark/get-event-watermark/${eventId}`,
      },
      null,
      (data) => {
        updateWatermarkDetails(data);
      },
      (_) => { }
    );
  };

  const createWatermarkFormData = () => {
    const watermarkFormData = new FormData();
    watermarkType === "TEXT" &&
      watermarkFormData.append("watermarkText", enteredName);
    watermarkType === "IMAGE" &&
      watermarkImage &&
      watermarkFormData.append("watermarkImage", watermarkImage);
    !watermarkData?.watermarkUrl &&
      watermarkFormData.append("watermarkType", watermarkType);
    watermarkFormData.append("position", watermarkPosition);
    watermarkFormData.append("eventId", eventId);
    watermarkFormData.append("fontSize", watermarkFontSize);
    watermarkFormData.append("isActive", true);
    return watermarkFormData;
  };

  const updateWatermarkDetails = (data) => {
    setWatermarkData(data?.data);
    setWatermarkType(data?.data?.watermarkType);
    setWatermarkImagePrev(data?.data?.watermarkUrl);
    setWatermarPosition(data?.data?.position);
    setWatermarkFontSize(data?.data?.fontSize);
  };

  const createWatermarkClickHandler = () => {
    if (watermarkType === "TEXT" && !isValidWatermarkText(enteredName)) {
      Notifications.toast.error({
        title: "Watermark text contains inappropriate or invalid content",
      });
      return;
    }

    createWatermarkRequest(
      {
        url: "watermark/create_watermark",
        method: "POST",
        body: createWatermarkFormData(),
        formData: true,
      },
      null,
      (data) => {
        Notifications.toast.success(data.message);
        props?.onSuccess && props?.onSuccess();
        props?.onCancel && props?.onCancel();
        // if (premiumPlanId == null) {
        //   dispatch(setPremiumAlertModalClose(false));
        // }
      },
      (err, data) => {
        Notifications.toast.error(data.message);
      }
    );
  };

  const updateWatermarkClickHandler = () => {
    if (watermarkType === "TEXT" && !isValidWatermarkText(enteredName)) {
      Notifications.toast.error({
        title: "Watermark text contains inappropriate or invalid content",
      });
      return;
    }
    updateWatermarkRequest(
      {
        url: `watermark/update_event_watermark`,
        method: "PATCH",
        body: createWatermarkFormData(),
        formData: true,
      },
      null,
      (data) => {
        updateWatermarkDetails(data);
        Notifications.toast.success(data.message);
        props?.onCancel && props?.onCancel();
      },
      (err, data) => {
        Notifications.toast.error(data.message);
      }
    );
  };

  const deleteWatermarkClickHandler = () => {
    deleteWatermarkRequest(
      {
        url: `watermark/delete-event-watermark/${eventId}`,
        method: "DELETE",
      },
      null,
      (data) => {
        props?.onDelete && props?.onDelete();
        props?.onCancel && props?.onCancel();
        Notifications.toast.success(data?.message);
      },
      (err, data) => {
        Notifications.toast.error(data?.message);
      }
    );
  };

  useEffect(() => {
    getWatermarkRequestHandler();
  }, []);

  return (
    <>
      <Container
        fullWidth
        direction="column"
        horizontalAlign="center"
        gap={"40px"}
      >
        <>
          <Container fullWidth style={{ maxWidth: "300px" }}>
            {/* <Text
              appearance="body-m-bold"
              color="primary-grey-80"
              text="Upload your own Watermark or type some text"
              textAlign="center"
            /> */}
          </Container>
          {
            <Box width={450} position={"relative"} sx={{ '& img': { display: 'block', width: '100%', height: '100%', objectFit: 'cover' } }}>
              {/* <CardOverlay /> */}
              <img src={previewImg} alt="preview image" />
              <Box
                position={"absolute"}
                class={`${classes["position-item"]} ${classes[watermarkPosition]}`}
              >
                {watermarkType === "TEXT" && (
                  <Box sx={{ '& .text-atom': { fontSize: watermarkFontSize + 'px' } }}>
                    <Text text={enteredName} textAlign="center" color="white" appearance="body-m" />
                  </Box>
                )}
                {watermarkType === "IMAGE" && watermarkImagePrev && (
                  <Box width={100}>
                    <img src={watermarkImagePrev} />
                  </Box>
                )}
              </Box>
            </Box>
            //   : (
            //     <Box width={176}>
            //       <img src={defaultImg} alt="no frame state img" />
            //     </Box>
            //   )
          }
        </>
        <Container fullWidth direction="column" gap={24}>
          {/* {!isWatermarkDetailsLoading && (
            <RadioGroup
              {...radioGroupProps}
              selectedValue={watermarkType ? watermarkType : undefined}
              onChange={(value) => {
                setWatermarkType(value);
                // if (value === "TEXT") setWatermarkImagePrev(null);
              }}
            />
          )} */}
          {/* <InputDropdown
            value={watermarkPosition}
            label="Watermark Position"
            items={[
              { label: "Top Left", value: "TOP_LEFT" },
              { label: "Top Center", value: "TOP_CENTER" },
              { label: "Top Right", value: "TOP_RIGHT" },
              { label: "Center Left", value: "CENTER_LEFT" },
              { label: "Center Center", value: "CENTER" },
              { label: "Center Right", value: "CENTER_RIGHT" },
              { label: "Bottom Left", value: "BOTTOM_LEFT" },
              { label: "Bottom Center", value: "BOTTOM_CENTER" },
              { label: "Bottom Right", value: "BOTTOM_RIGHT" },
            ]}
            onSelect={(index, value) => {
              setWatermarPosition(value);
            }}
          /> */}
          {watermarkType === "TEXT" && (
            <InputField
              label="Watermark Text"
              placeholder={enteredName}
              // value={enteredName}
              onChange={nameChangeHandler}
              onBlur={nameBlurHandler}
              state={enteredNameHasError ? "error" : "none"}
              helperText={`Min 3 and Max 25 characters is required ( ${enteredName ? enteredName.length : 0
                } / 25 )`}
              stateConfig={{ errorText: "Please enter a valid and appropriate text!" }}
            />
          )}
          {watermarkType === "IMAGE" && (
            <Container fullWidth horizontalAlign="center">
              <input
                type="file"
                id="watermark"
                name="watermark_img"
                accept=".jpeg,.jpg,.png"
                style={{ display: "none" }}
                onInput={(e) => {
                  hadleFrameFileChange(e);
                }}
                ref={watermarkFileInputRef}
              />
              <Button
                label="Upload Watermark"
                iconLeft={"IcAdd"}
                onClick={handleUploadFrameClick}
              />
            </Container>
          )}

          <InputDropdown
            value={watermarkFontSize}
            label="Font Size"
            items={[
              { label: "14", value: 14 },
              { label: "16", value: 16 },
              { label: "18", value: 18 },
              { label: "20", value: 20 },
              { label: "22", value: 22 },
              { label: "24", value: 24 },
              { label: "26", value: 26 },
            ]}
            onSelect={(index, value) => {
              setWatermarkFontSize(value);
            }}
          />
        </Container>
        <Container fullWidth horizontalAlign="center" gap={16}>
          {watermarkData ? (
            <Button
              label="Update"
              disabled={!isFormValid}
              onClick={updateWatermarkClickHandler}
              loading={isWatermarkUpdating}
              size="large"
            />
          ) : (
            <Button
              label="Create"
              disabled={!isFormValid}
              onClick={createWatermarkClickHandler}
              loading={isWatermarkCreating}
              size="large"
            />
          )}
          <Button
            label="Delete"
            kind="secondary"
            // onClick={() => props?.onCancel && props?.onCancel()}
            onClick={deleteWatermarkClickHandler}
            state="destructive"
            loading={isWatermarkDeleting}
            size="large"
          />
        </Container>
      </Container>
      <PageLoader open={isWatermarkDetailsLoading} />
    </>
  );
}
